// Computed values and transformations for dashboard stat grid.

import { ref, computed } from '@vue/composition-api';
import { formatCurrency as formatCurrencyDisplay } from '../components/Utils.js';
import { resolveSavedSearchNewMatches } from './useSavedSearchNewMatches.js';

/** Competitor expiring `urgency` values that map to the ~3‑month bucket (current API + legacy). */
const COMPETITOR_EXPIRING_URGENT_3MO = new Set(['urgent_3mo', '90_days']);

/**
 * Composable for dashboard stat grid computations and transformations.
 * Assumes data is provided by an external useDailyDigest instance (e.g. from Dashboard.vue).
 * Provides computed properties and methods specific to the stat grid UI.
 * 
 * @param {Object} params - Configuration
 * @param {Object} params.dailyDigest - dailyDigest ref from useDailyDigest
 * @param {Object} params.loading - loading ref from useDailyDigest
 * @param {Function} params.load - load function from useDailyDigest
 * @returns {Object} Object containing computed properties and methods for stat grid
 */
export function useDashboardStatGrid({ dailyDigest, loading, load }) {
  // Reuse the shared daily digest composable instance provided by the caller.
  const loadingDashboardStatGrid = loading;
  const loadDashboardStatGrid = load;
  
  // Number of items to display per page in dashboard sections
  const sectionPageSize = ref(10);

  // ============================================================================
  // Computed Properties
  // ============================================================================

  /**
   * Checks if there are any high priority market patterns in the dashboard data.
   * @returns {boolean} True if any pattern has priority === 'high', false otherwise
   */
  const hasHighPriorityPatterns = computed(() => {
    return (dailyDigest.value.market_patterns && dailyDigest.value.market_patterns.some(p => p.priority === 'high')) || false;
  });

  /**
   * Counts the number of expiring contracts.
   * @returns {number} Total count of expiring contracts, or 0 if none
   */
  const expiringCount = computed(() => {
    return (dailyDigest.value.expiring_contracts && dailyDigest.value.expiring_contracts.length) || 0;
  });

  /**
   * Counts total competitor-related activities (activity + expiring contracts).
   * @returns {number} Sum of competitor activity count and competitor expiring contracts count
   */
  const competitorCount = computed(() => {
    const activityCount = (dailyDigest.value.competitor_activity && dailyDigest.value.competitor_activity.length) || 0;
    const expiringCount = (dailyDigest.value.competitor_expiring_contracts && dailyDigest.value.competitor_expiring_contracts.length) || 0;
    return activityCount + expiringCount;
  });

  /**
   * Filters buyer activity to exclude keyword matches (Opportunity cluster type).
   * Returns only tracked buyer activities from configured clusters.
   * @returns {Array} Filtered array of buyer activities without keyword matches
   */
  const trackedBuyerActivity = computed(() => {
    if (!dailyDigest.value.buyer_activity) return [];
    // Filter out keyword matches (cluster_type === 'Opportunity')
    return dailyDigest.value.buyer_activity.filter(activity => activity.cluster_type !== 'Opportunity');
  });

  /**
   * Filters buyer activity to include only keyword matches (Opportunity cluster type).
   * These are opportunities found through keyword matching rather than tracked clusters.
   * @returns {Array} Filtered array containing only keyword-matched opportunities
   */
  const keywordMatches = computed(() => {
    if (!dailyDigest.value.buyer_activity) return [];
    // Only include keyword matches (cluster_type === 'Opportunity')
    return dailyDigest.value.buyer_activity.filter(activity => activity.cluster_type === 'Opportunity');
  });

  /**
   * Counts the number of keyword-matched opportunities.
   * @returns {number} Total count of keyword matches
   */
  const keywordMatchesCount = computed(() => {
    return keywordMatches.value.length;
  });

  /**
   * Counts total buyer-related activities (tracked buyers + keyword matches).
   * @returns {number} Sum of tracked buyer activities and keyword matches
   */
  const buyerCount = computed(() => {
    const trackedCount = trackedBuyerActivity.value.length;
    const keywordCount = keywordMatchesCount.value;
    return trackedCount + keywordCount;
  });

  /**
   * Counts saved-search new matches (digest list length).
   * @returns {number} Total count, or 0 if none
   */
  const matchesCount = computed(() => {
    return resolveSavedSearchNewMatches(dailyDigest.value).length;
  });

  /**
   * Counts total panel intelligence items across all categories.
   * Includes: competitors recently joined panels, suggested panels, and newly created panels.
   * @returns {number} Sum of all panel intelligence items, or 0 if none
   */
  const panelIntelligenceCount = computed(() => {
    if (!dailyDigest.value.panel_intelligence) return 0;
    const pi = dailyDigest.value.panel_intelligence;
    const recentlyJoined = (pi.competitors_recently_joined && pi.competitors_recently_joined.length) || 0;
    const suggested = (pi.suggested_panels && pi.suggested_panels.length) || 0;
    const newlyCreated = (pi.newly_created_panels && pi.newly_created_panels.length) || 0;
    return recentlyJoined + suggested + newlyCreated;
  });

  /**
   * Counts the number of industry matches.
   * @returns {number} Total count of industry matches, or 0 if none
   */
  const industryMatchesCount = computed(() => {
    return (dailyDigest.value.industry_matches && dailyDigest.value.industry_matches.length) || 0;
  });

  /**
   * Counts competitor contracts in the urgent ~3‑month expiry bucket.
   * @returns {number} Count of competitor expiring contracts with matching urgency
   */
  const competitorExpiringWithin3Months = computed(() => {
    const list = dailyDigest.value.competitor_expiring_contracts;
    if (!Array.isArray(list)) return 0;
    return list.filter((contract) => COMPETITOR_EXPIRING_URGENT_3MO.has(contract.urgency)).length;
  });

  /**
   * Generates an array of stat card configurations for the dashboard stats grid.
   * Each card includes icon, colors, values, labels, subtext, and navigation information.
   * Cards are: New Matches, Expiring Soon, Competitors Activity, Buyers Activity, Total Pipeline, Market Signals.
   * @returns {Array<Object>} Array of stat card configuration objects
   */
  const statCards = computed(() => {
    const summary = dailyDigest.value.summary || {};
    const patterns = dailyDigest.value.market_patterns || [];
    const newMatchesCount = matchesCount.value;

    return [
      {
        icon: 'mdi-magnify',
        iconColor: '#ff1463',
        bgColor: '#ffebf3',
        value: newMatchesCount,
        label: 'New Matches',
        subtext:
          newMatchesCount > 0
            ? `${newMatchesCount} new ${newMatchesCount === 1 ? 'match' : 'matches'}`
            : 'No new matches',
        subtextColor: newMatchesCount > 0 ? '#ff1463' : '#6b7280',
        navTabKey: 'new_matches',
        isClickable: true
      },
      {
        icon: 'mdi-clock-outline',
        iconColor: '#ffe70a',
        bgColor: '#fff9e6',
        value: expiringCount.value || 0,
        label: 'Expiring Soon',
        subtext: formatCurrencyDisplay(summary.total_value_in_play || 0) + ' value',
        navTabKey: 'expiring',
        isClickable: true
      },
      {
        icon: 'mdi-flag',
        iconColor: '#12253a',
        bgColor: '#e6f0f5',
        value: competitorCount.value || 0,
        label: 'Competitors Activity',
        subtext: competitorExpiringWithin3Months.value > 0 ? `${competitorExpiringWithin3Months.value} ${competitorExpiringWithin3Months.value === 1 ? 'contract' : 'contracts'} expiring within 3 months` : 'No contracts expiring within 3 months',
        navTabKey: 'competitors_activity',
        isClickable: true
      },
      {
        icon: 'mdi-target',
        iconColor: '#10b981',
        bgColor: '#e6f5f0',
        value: buyerCount.value || 0,
        label: 'Buyers Activity',
        subtext: trackedBuyerActivity.value.length > 0 ? `${trackedBuyerActivity.value.length} ${trackedBuyerActivity.value.length === 1 ? 'new opportunity' : 'new opportunities'} from tracked buyers` : 'No new opportunities from tracked buyers',
        navTabKey: 'buyer_activity',
        isClickable: true
      },
      {
        icon: 'mdi-cash',
        iconColor: '#12253a',
        bgColor: '#e6f0f5',
        value: formatCurrencyDisplay(summary.total_value_in_play || 0),
        label: 'Total Pipeline',
        subtext: `${summary.urgent_expiring_count || 0} contracts expiring within 3 months`,
        navTabKey: 'expiring',
        isClickable: true
      },
      {
        icon: 'mdi-chart-line',
        iconColor: '#ff1463',
        bgColor: '#ffebf3',
        value: summary.pattern_count || 0,
        label: 'Market Signals',
        badgeColor: '#b71c1c',
        showSubtextAlert: summary.pattern_count > 0 && hasHighPriorityPatterns.value,
        subtext: summary.pattern_count > 0 && hasHighPriorityPatterns.value
          ? `${summary.pattern_count} high priority` 
          : summary.pattern_count > 0 
            ? `${summary.pattern_count} ${summary.pattern_count === 1 ? 'pattern' : 'patterns'}` 
            : 'No patterns detected',
        navTabKey: 'market_signals',
        isClickable: true
      }
    ];
  });

  /**
   * Determines the display order of dashboard sections based on data availability.
   * Sections with data are prioritized and appear first, followed by sections without data.
   * Used to dynamically reorder sections in the "All Activity" tab view.
   * @returns {Array<string>} Array of section keys ordered by data availability
   */
  const orderedSections = computed(() => {
    // This will be used with activeTab from navigation composable
    // For now, return sections ordered by data availability
    const sections = [
      {
        key: 'new_matches',
        hasData: resolveSavedSearchNewMatches(dailyDigest.value).length > 0,
      },
      { key: 'expiring', hasData: (dailyDigest.value.expiring_contracts && dailyDigest.value.expiring_contracts.length > 0) },
      { key: 'competitors_activity', hasData: (dailyDigest.value.competitor_activity && dailyDigest.value.competitor_activity.length > 0) },
      { key: 'buyer_activity', hasData: trackedBuyerActivity.value.length > 0 },
      { key: 'keyword_matches', hasData: keywordMatchesCount.value > 0 },
      { key: 'industry_matches', hasData: (dailyDigest.value.industry_matches && dailyDigest.value.industry_matches.length > 0) },
      { key: 'new_entrants', hasData: (dailyDigest.value.new_entrants && dailyDigest.value.new_entrants.length > 0) },
      { key: 'competitor_expiring', hasData: (dailyDigest.value.competitor_expiring_contracts && dailyDigest.value.competitor_expiring_contracts.length > 0) },
      { key: 'panel_intelligence', hasData: panelIntelligenceCount.value > 0 }
    ];

    // Sort: sections with data first, then sections without data
    return sections.sort((a, b) => {
      if (a.hasData && !b.hasData) return -1;
      if (!a.hasData && b.hasData) return 1;
      return 0;
    }).map(s => s.key);
  });

  /**
   * Formats a numeric value as currency using the formatCurrencyDisplay utility.
   * Wrapper function that delegates to the Utils.js currency formatter.
   * @param {number} value - The numeric value to format
   * @returns {string} Formatted currency string (e.g., "$1.5M", "$5.2K", "$1,234")
   */
  const formatCurrency = (value) => {
    return formatCurrencyDisplay(value);
  };

  // ============================================================================
  // Return Public API
  // ============================================================================
  
  /**
   * Returns all computed properties and methods for use in components.
   * This composable provides stat grid specific computations.
   * For raw data access, use dailyDigest from useDailyDigest directly.
   */
  return {
    // Expose daily digest for direct data access (backward compatibility as dashboardStatGrid)
    dashboardStatGrid: dailyDigest,
    loadingDashboardStatGrid,
    sectionPageSize,
    // Methods
    loadDashboardStatGrid,
    formatCurrency,
    // Computed properties
    hasHighPriorityPatterns,
    expiringCount,
    competitorCount,
    trackedBuyerActivity,
    keywordMatches,
    keywordMatchesCount,
    buyerCount,
    matchesCount,
    panelIntelligenceCount,
    industryMatchesCount,
    competitorExpiringWithin3Months,
    statCards,
    orderedSections
  };
}
